// The Shifts screen's server state (spec/13 §10, F-OPS-01 … F-OPS-07), as TanStack Query hooks over
// the generated client — `packages/contracts/src/routes/shifts.ts`:
//
//   GET  …/venues/{v}/shifts/current            the venue's open shift            shifts:own
//   GET  …/venues/{v}/shifts                    the venue's shifts                shifts:read_all
//   GET  …/venues/{v}/shifts/{id}               one shift with its X/Z report     shifts:read_all
//   GET  …/venues/{v}/shifts/{id}/cash-timeline the drawer's events, in order     shifts:own (own) / shifts:read_all
//   POST …/venues/{v}/shifts                    open a shift with a float         shifts:own
//   POST …/venues/{v}/shifts/{id}/movements     a pay-in, pay-out or drop         shifts:cash_movements
//   POST …/venues/{v}/shifts/{id}/close         the blind count and the variance  shifts:own
//   GET  …/venues/{v}/day-close                 the day's review                  shifts:day_close
//   POST …/venues/{v}/day-close                 close the business day            shifts:day_close
//   POST …/venues/{v}/day-close/sign-off        sign off an automatic close       shifts:day_close
//   GET  …/venues/{v}/business-day              the day now, rollover, custody    venues:read
//   POST …/venues/{v}/drawer/open               Open drawer: a no-sale kick       shifts:drawer_open
//
// The drawer reconciles through the ledger on the server; the client only opens, moves cash, counts
// and closes (spec/13 §10).

import type {
  BusinessDayClose,
  Counter,
  CounterBoard,
  CreateCounterRequest,
  UpdateCounterRequest,
  BusinessDayStatus,
  CloseBusinessDayRequest,
  CloseShiftRequest,
  DayCloseReview,
  DrawerOpen,
  OpenShiftRequest,
  RecordCashMovementRequest,
  Shift,
  ShiftCashTimeline,
  ShiftCashTimelineTenders,
  ShiftList,
} from '@clavian/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useApi } from '../../api/client';
import { PROBLEM, problemCode, problemStatus } from '../../api/problems';

export const shiftKeys = {
  current: (organizationId: string, venueId: string) => ['shifts', organizationId, venueId, 'current'] as const,
  list: (organizationId: string, venueId: string) => ['shifts', organizationId, venueId, 'list'] as const,
  dayClose: (organizationId: string, venueId: string) => ['shifts', organizationId, venueId, 'day-close'] as const,
  businessDay: (organizationId: string, venueId: string) => ['shifts', organizationId, venueId, 'business-day'] as const,
  timeline: (organizationId: string, venueId: string) => ['shifts', organizationId, venueId, 'cash-timeline'] as const,
  counters: (organizationId: string, venueId: string) => ['shifts', organizationId, venueId, 'counters'] as const,
};

/** The timeline's filter: cash only (the default) or every tender, and one order by its number. */
export interface CashTimelineFilter {
  readonly tenders: ShiftCashTimelineTenders;
  readonly orderNumber?: number;
}

/** How many events the timeline reads per page; the CSV reads every page at the largest size the contract allows. */
const TIMELINE_PAGE = 50;
const TIMELINE_EXPORT_PAGE = 100;

/**
 * A shift's cash timeline (F-OPS-04): every drawer event in order with the running expected drawer, a page at a time.
 * `shifts:own` reads the caller's own shift; another member's needs `shifts:read_all` — a 403 is the answer otherwise.
 */
export function useShiftCashTimeline(organizationId: string, venueId: string, shiftId: string, filter: CashTimelineFilter, enabled = true) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [...shiftKeys.timeline(organizationId, venueId), shiftId, filter.tenders, filter.orderNumber ?? null],
    enabled,
    queryFn: ({ pageParam }): Promise<ShiftCashTimeline> =>
      api.getShiftCashTimeline({
        path: { organizationId, venueId, shiftId },
        query: { limit: TIMELINE_PAGE, tenders: filter.tenders, ...(filter.orderNumber === undefined ? {} : { orderNumber: filter.orderNumber }), ...(pageParam === undefined ? {} : { cursor: pageParam }) },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
    retry: (count, error) => problemStatus(error) !== 403 && count < 2,
  });
}

/** Every event of the timeline under the filter, every page — what the CSV download writes. */
export function useReadWholeCashTimeline(organizationId: string, venueId: string, shiftId: string) {
  const api = useApi();
  return async (filter: CashTimelineFilter): Promise<ShiftCashTimeline['data']> => {
    const events: ShiftCashTimeline['data'] = [];
    let cursor: string | undefined;
    do {
      const page = await api.getShiftCashTimeline({
        path: { organizationId, venueId, shiftId },
        query: { limit: TIMELINE_EXPORT_PAGE, tenders: filter.tenders, ...(filter.orderNumber === undefined ? {} : { orderNumber: filter.orderNumber }), ...(cursor === undefined ? {} : { cursor }) },
      });
      events.push(...page.data);
      cursor = page.page.hasMore && page.page.cursor !== null ? page.page.cursor : undefined;
    } while (cursor !== undefined);
    return events;
  };
}

function isNotFound(error: unknown): boolean {
  return problemStatus(error) === 404 || problemCode(error) === PROBLEM.notFound;
}

/** The venue's open shift, or `null` when none is open (a 404 is not an error here). */
export function useCurrentShift(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery<Shift | null>({
    queryKey: shiftKeys.current(organizationId, venueId),
    enabled,
    queryFn: async () => {
      try {
        return await api.getCurrentShift({ path: { organizationId, venueId } });
      } catch (error) {
        if (isNotFound(error)) return null;
        throw error;
      }
    },
  });
}

/** The venue's shifts, newest first (F-OPS-04). Needs `shifts:read_all`; skip the read without it. */
export function useShifts(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery<ShiftList>({
    queryKey: shiftKeys.list(organizationId, venueId),
    enabled,
    queryFn: () => api.listShifts({ path: { organizationId, venueId } }),
  });
}

/** One shift with its X/Z report — expected vs counted per tender, the note, who closed it (F-OPS-04, -07). */
export function useShift(organizationId: string, venueId: string, shiftId: string | undefined, enabled = true) {
  const api = useApi();
  return useQuery<Shift>({
    queryKey: [...shiftKeys.list(organizationId, venueId), 'one', shiftId ?? ''],
    enabled: enabled && shiftId !== undefined,
    queryFn: () => api.getShift({ path: { organizationId, venueId, shiftId: shiftId ?? '' } }),
  });
}

/**
 * A business day's review (F-OPS-06): takings, corrections and its shifts — today's, or `businessDay`'s (a day that
 * closed at its rollover and waits for a sign-off, ADR-0029). Needs `shifts:day_close`.
 */
export function useDayCloseReview(organizationId: string, venueId: string, enabled = true, businessDay?: string) {
  const api = useApi();
  return useQuery<DayCloseReview>({
    queryKey: businessDay === undefined ? shiftKeys.dayClose(organizationId, venueId) : [...shiftKeys.dayClose(organizationId, venueId), businessDay],
    enabled,
    queryFn: () => api.getBusinessDayClose({ path: { organizationId, venueId }, ...(businessDay === undefined ? {} : { query: { businessDay } }) }),
  });
}

/**
 * The business day now (ADR-0029): which day it is, when and how it rolls over, whether drawers are tracked, who holds
 * the drawer, and the days that need a manager. The status strip on Sell and Shifts reads it; any member of the venue
 * may (`venues:read`), the drawer only shown to who may see shifts. Refetched each minute so the strip rolls over.
 */
export function useBusinessDayStatus(organizationId: string, venueId: string, enabled = true) {
  const api = useApi();
  return useQuery<BusinessDayStatus>({
    queryKey: shiftKeys.businessDay(organizationId, venueId),
    enabled,
    refetchInterval: 60_000,
    queryFn: () => api.getBusinessDayStatus({ path: { organizationId, venueId } }),
  });
}

function useShiftInvalidation(organizationId: string, venueId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: shiftKeys.current(organizationId, venueId) });
    void queryClient.invalidateQueries({ queryKey: shiftKeys.list(organizationId, venueId) });
    void queryClient.invalidateQueries({ queryKey: shiftKeys.dayClose(organizationId, venueId) });
    void queryClient.invalidateQueries({ queryKey: shiftKeys.businessDay(organizationId, venueId) });
    void queryClient.invalidateQueries({ queryKey: shiftKeys.timeline(organizationId, venueId) });
    void queryClient.invalidateQueries({ queryKey: shiftKeys.counters(organizationId, venueId) });
  };
}

/**
 * The venue's counters, each with its running drawer (ADR-0032): the Start your shift picker, the running counters
 * panel and Settings → Counters. Refetched every 20 seconds while shown, and on every shift change made here.
 */
export function useCounters(organizationId: string, venueId: string, options: { readonly includeArchived?: boolean; readonly enabled?: boolean; readonly live?: boolean } = {}) {
  const api = useApi();
  const includeArchived = options.includeArchived === true;
  return useQuery<CounterBoard>({
    queryKey: [...shiftKeys.counters(organizationId, venueId), includeArchived],
    enabled: options.enabled ?? true,
    ...(options.live === true ? { refetchInterval: 20_000 } : {}),
    queryFn: () => api.listCounters({ path: { organizationId, venueId }, ...(includeArchived ? { query: { includeArchived: 'true' as const } } : {}) }),
  });
}

/** Add a counter (`settings:payments`). */
export function useCreateCounter(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useShiftInvalidation(organizationId, venueId);
  return useMutation<Counter, unknown, CreateCounterRequest>({
    mutationFn: (body) => api.createCounter({ path: { organizationId, venueId }, body }),
    onSuccess: invalidate,
  });
}

/** Rename, re-float, link, archive or restore a counter (`settings:payments`). */
export function useUpdateCounter(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useShiftInvalidation(organizationId, venueId);
  return useMutation<Counter, unknown, { readonly counterId: string; readonly body: UpdateCounterRequest }>({
    mutationFn: ({ counterId, body }) => api.updateCounter({ path: { organizationId, venueId, counterId }, body }),
    onSuccess: invalidate,
  });
}

/** Open a shift with an opening float (F-OPS-01). */
export function useOpenShift(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useShiftInvalidation(organizationId, venueId);
  return useMutation<Shift, unknown, OpenShiftRequest>({
    mutationFn: (body) => api.openShift({ path: { organizationId, venueId }, headerParams: { 'idempotency-key': crypto.randomUUID() }, body }),
    onSuccess: invalidate,
  });
}

/** Record a pay-in, pay-out or drop on the open drawer (F-OPS-02). */
export function useRecordCashMovement(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useShiftInvalidation(organizationId, venueId);
  return useMutation<Shift, unknown, { shiftId: string; body: RecordCashMovementRequest }>({
    mutationFn: ({ shiftId, body }) => api.recordCashMovement({ path: { organizationId, venueId, shiftId }, body }),
    onSuccess: invalidate,
  });
}

/** Close a shift with the blind count and the variance (F-OPS-03, F-OPS-05). */
export function useCloseShift(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useShiftInvalidation(organizationId, venueId);
  return useMutation<Shift, unknown, { shiftId: string; body: CloseShiftRequest }>({
    mutationFn: ({ shiftId, body }) => api.closeShift({ path: { organizationId, venueId, shiftId }, headerParams: { 'idempotency-key': crypto.randomUUID() }, body }),
    onSuccess: invalidate,
  });
}

/** Close the business day once every shift is closed (F-OPS-06). */
export function useCloseBusinessDay(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useShiftInvalidation(organizationId, venueId);
  return useMutation<BusinessDayClose, unknown, CloseBusinessDayRequest>({
    mutationFn: (body) => api.closeBusinessDay({ path: { organizationId, venueId }, headerParams: { 'idempotency-key': crypto.randomUUID() }, body }),
    onSuccess: invalidate,
  });
}

/** Join a shared drawer's shift, or leave it (ADR-0029, `shifts.cashHandling: shared_drawer`). */
export function useShiftMembership(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useShiftInvalidation(organizationId, venueId);
  return useMutation<Shift, unknown, { readonly shiftId: string; readonly action: 'join' | 'leave' }>({
    mutationFn: ({ shiftId, action }) =>
      action === 'join' ? api.joinShift({ path: { organizationId, venueId, shiftId } }) : api.leaveShift({ path: { organizationId, venueId, shiftId } }),
    onSuccess: invalidate,
  });
}

/** Sign off a business day that closed at its rollover (ADR-0029): the manager has read its report. */
export function useSignOffBusinessDay(organizationId: string, venueId: string) {
  const api = useApi();
  const invalidate = useShiftInvalidation(organizationId, venueId);
  return useMutation<BusinessDayClose, unknown, { readonly businessDay: string; readonly note?: string }>({
    mutationFn: (body) => api.signOffBusinessDay({ path: { organizationId, venueId }, body }),
    onSuccess: invalidate,
  });
}

/**
 * Open drawer (F-OPS-09): a no-sale kick through the desk printer the cash drawer is wired to. The server audits it
 * with the open shift; the kick's outcome arrives on `command.updated` (`../printing/command-status.ts`).
 */
export function useOpenDrawer(organizationId: string, venueId: string) {
  const api = useApi();
  return useMutation<DrawerOpen, unknown, { readonly reason?: string }>({
    mutationFn: ({ reason }) => api.openDrawer({ path: { organizationId, venueId }, body: reason === undefined ? {} : { reason } }),
  });
}
